// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Breadcrumbs, { BreadcrumbsPresentationProps } from "@eisinc/MobiusOverrides/Breadcrumbs";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getHomePageUrl, getPageLinkByNodeId } from "@insite/client-framework/Store/Links/LinksSelectors";
import LinksState from "@insite/client-framework/Store/Links/LinksState";
import translate from "@insite/client-framework/Translate";
import VmiPageTypes from "@insite/client-framework/Types/VmiPageTypes";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import { LinkProps } from "@insite/mobius/Link";
import React from "react";
import { connect } from "react-redux";

interface OwnProps {
    extendedStyles?: PageBreadcrumbsStyles;
}

const mapStateToProps = (state: ApplicationState) => {
    return {
        links: state.components.breadcrumbs.links,
        linksState: state.links,
        nodeId: getCurrentPage(state).nodeId,
        homePageUrl: getHomePageUrl(state),
        // XNG-Change
        isPdp: getCurrentPage(state).type === "ProductDetailsPage",
    };
};

export interface PageBreadcrumbsStyles {
    breadcrumbs?: BreadcrumbsPresentationProps;
    breadcrumbsPlaceholder?: BreadcrumbsPresentationProps;
}

export const pageBreadcrumbStyles: PageBreadcrumbsStyles = {};

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

const PageBreadcrumbs = (props: Props) => {
    // here linksState hase arelinksloading
    const links = props.links || generateLinksFrom(props.linksState, props.nodeId, props.homePageUrl);

    const styles = useMergeStyles("pageBreadcrumbs", pageBreadcrumbStyles, props.extendedStyles);

    // XNG-Change
    if (props.linksState.areLinksLoading) {
        return <StyledWrapper {...styles.breadcrumbsPlaceholder} />;
    }
    return (
        <Breadcrumbs
            links={links}
            data-test-selector="pageBreadcrumbs"
            {...styles.breadcrumbs}
            // XNG-Change
            hideCurrentPage={props.isPdp}
        />
    );
};

export function generateLinksFrom(linksState: LinksState, nodeId: string, homePageUrl: string) {
    const links: LinkProps[] = [];
    let currentLink = getPageLinkByNodeId({ links: linksState }, nodeId);

    const vmiExcludePageTypes: string[] = ["HomePage", "MyAccountPage"];
    const search = (targetText: string) => (sourceTextArray: string) => sourceTextArray === targetText;
    const isVmiPageType = !currentLink?.type ? false : VmiPageTypes.some(search(currentLink.type));
    while (currentLink) {
        const isVmiExcludePageType = !currentLink?.type ? false : vmiExcludePageTypes.some(search(currentLink.type));
        if (!isVmiPageType || !isVmiExcludePageType) {
            links.unshift({
                children: currentLink.type === "HomePage" ? translate("Home_Breadcrumb") : currentLink.title,
                href: currentLink.url,
                type: currentLink.type,
            });
        }
        currentLink = currentLink.parentId
            ? getPageLinkByNodeId({ links: linksState }, currentLink.parentId)
            : undefined;
    }

    return links;
}

export default connect(mapStateToProps)(PageBreadcrumbs);
